import React, { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import AppleIcon from '@mui/icons-material/Apple';
import {
  Box,
  Button,
  Card,
  CardContent,
  Fade,
  Grid,
  Hidden,
  IconButton,
  InputAdornment,
  Link as MUILink,
  Slide,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { APIEndpoint } from 'common/enum';
import { LoginResponse } from 'common/interface';
import { universalPostRequest } from 'common/requestHandler';
import { logoutUserOtherSessions } from 'common/utils';
import { popupNotification } from 'notifications/ToastNotification';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { validateUsernameOrEmail } from 'screens/superAdmin/validation';
import colors from 'theme/colors';
import * as yup from 'yup';

import GooglePlayIcon from '../../assets/icons/google.png';
import docImage from '../../assets/images/login2.svg';
import logo from '../../assets/images/logo.png';
import CustomModal from '../../components/CustomModal';
import GradientButton from '../../components/elements/GradientButton';
import InputElement from '../../components/elements/InputElement';
import { login, logout } from '../../redux/slices/authSlice';

// Modern styled components
const ModernContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: '#F9F9F9',
  position: 'relative',
  overflow: 'hidden',
}));

const GlassCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: 24,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  padding: theme.spacing(4),
  maxWidth: 480,
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #42d392, #647eff)',
  },
}));

const FloatingShape = styled(Box)(({ theme }) => ({
  position: 'absolute',
  borderRadius: '50%',
  background:
    'linear-gradient(45deg, rgba(66, 211, 146, 0.1), rgba(100, 126, 255, 0.1))',
  animation: 'float 6s ease-in-out infinite',
  '&:nth-of-type(1)': {
    width: 120,
    height: 120,
    top: '10%',
    left: '10%',
    animationDelay: '0s',
  },
  '&:nth-of-type(2)': {
    width: 80,
    height: 80,
    top: '60%',
    right: '15%',
    animationDelay: '2s',
  },
  '&:nth-of-type(3)': {
    width: 100,
    height: 100,
    bottom: '20%',
    left: '20%',
    animationDelay: '4s',
  },
  '@keyframes float': {
    '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
    '50%': { transform: 'translateY(-20px) rotate(180deg)' },
  },
}));

const ModernTypography = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(45deg, #42d392, #647eff)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  fontWeight: 700,
  letterSpacing: '-0.02em',
}));

const HelpLink = styled(Button)(({ theme }) => ({
  color: colors.primary,
  textDecoration: 'none',
  fontWeight: 500,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-1px)',
    textShadow: '0 2px 4px rgba(18, 112, 220, 0.3)',
  },
}));

const ForgotLink = styled(Link)(({ theme }) => ({
  color: colors.primary,
  textDecoration: 'none',
  fontWeight: 500,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-1px)',
    textShadow: '0 2px 4px rgba(18, 112, 220, 0.3)',
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'whitesmoke',
  position: 'relative',
  overflow: 'hidden',
}));

const AnimatedLockContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
}));

interface LoginFormInputs {
  username: string;
  password: string;
}

const schema = yup.object().shape({
  username: validateUsernameOrEmail,
  password: yup.string().required('Password is required'),
});

const LoginPage: React.FC = () => {
  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<LoginFormInputs>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      username: '',
      password: '',
    },
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAlreadyLoggedInModal, setShowAlreadyLoggedInModal] =
    useState(false);
  const [isFirstLogIn, setIsFirstLogin] = useState(false);
  const [loggingOutUser, setLoggingOutUser] = useState(false);
  const [username, setUsername] = useState('');

  const onSubmit: SubmitHandler<LoginFormInputs> = async (data) => {
    setLoading(true);
    const response = await universalPostRequest<LoginResponse>(
      APIEndpoint.login,
      {
        username: data.username,
        password: data.password,
      }
    );
    setLoading(false);
    if ('error' in response) {
      popupNotification(response.error, false);
    } else {
      dispatch(
        login({
          accessToken: response.data.access_token,
          refreshToken: response.data.refresh_token,
          idToken: response.data.id_token,
          expiresIn: response.data.expires_in,
          date: response.data.date,
          deviceKey: response.data.device_key,
          secretCode: response.data.secret_code,
          username: response.data.username,
          isFirstLogin: response.data.is_first_login,
          isMfaActive: response.data.is_mfa_active,
          sessionKey: response.data.session_key,
        })
      );
      setIsFirstLogin(response.data.is_user_loggedin);

      if (response.data.is_user_loggedin) {
        setShowAlreadyLoggedInModal(true);
        setUsername(response.data.username);
        return;
      }

      if (response.data.is_first_login) {
        navigate('/mfa-config');
      } else {
        navigate('/verify-mfa');
      }
    }
  };

  const handleRedirection = async () => {
    setLoggingOutUser(true);
    await logoutUserOtherSessions(username, setLoggingOutUser);
    setLoggingOutUser(false);
    setShowAlreadyLoggedInModal(false);
    const values = getValues();
    onSubmit(values);
  };

  return (
    <ModernContainer>
      {/* Floating decorative shapes */}

      <Grid
        container
        style={{ height: '100vh', position: 'relative', zIndex: 1 }}
      >
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            p: 3,
          }}
        >
          <Slide direction="down" in={true} timeout={800}>
            <Box display="flex" justifyContent="center" mb={4}>
              <img
                src={logo}
                alt="Logo"
                height={100}
                style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' }}
              />
            </Box>
          </Slide>

          <Fade in={true} timeout={1000}>
            <GlassCard>
              <CardContent sx={{ p: 0 }}>
                <ModernTypography
                  variant="h3"
                  gutterBottom
                  align="center"
                  sx={{ mb: 4 }}
                >
                  Welcome Back
                </ModernTypography>

                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  align="center"
                  sx={{ mb: 4 }}
                >
                  Sign in to your account to continue
                </Typography>

                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                  <Box sx={{ mb: 3 }}>
                    <InputElement
                      name="username"
                      control={control}
                      required
                      label="Username or Email"
                      type="text"
                      autoFocus
                      rules={{ required: true }}
                    />
                  </Box>

                  <Box sx={{ mb: 4 }}>
                    <InputElement
                      name="password"
                      control={control}
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      rules={{ required: true }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword((prev) => !prev)}
                              edge="end"
                              size="small"
                              sx={{
                                color: colors.primary,
                                '&:hover': {
                                  backgroundColor: 'rgba(18, 112, 220, 0.1)',
                                },
                              }}
                            >
                              {showPassword ? (
                                <VisibilityOff fontSize="small" />
                              ) : (
                                <Visibility fontSize="small" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Box sx={{ mb: 4 }}>
                    <GradientButton
                      fullWidth
                      type="submit"
                      loading={loading}
                      rounded={true}
                      sx={{
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                      }}
                    >
                      Sign in
                    </GradientButton>
                  </Box>

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    flexWrap="wrap"
                    gap={2}
                  >
                    <HelpLink
                      onClick={() => setShowModal(true)}
                      sx={{ fontSize: '0.9rem' }}
                    >
                      Need help?
                    </HelpLink>
                    <Box display="flex" flexDirection="column" gap={1}>
                      <ForgotLink
                        to="/forgot-username"
                        sx={{ fontSize: '0.9rem' }}
                      >
                        Forgot username
                      </ForgotLink>
                      <ForgotLink
                        to="/forgot-password"
                        sx={{ fontSize: '0.9rem' }}
                      >
                        Forgot password
                      </ForgotLink>
                    </Box>
                  </Box>
                </form>
              </CardContent>
            </GlassCard>
          </Fade>
        </Grid>

        <Hidden smDown>
          <Grid item xs={12} md={6}>
            <RightPanel sx={{ height: '100%', p: 4 }}>
              <Slide direction="left" in={true} timeout={1200}>
                <Box
                  sx={{ textAlign: 'center', position: 'relative', zIndex: 2 }}
                >
                  <AnimatedLockContainer>
                    <ModernTypography
                      variant="h2"
                      sx={{
                        mb: 2,
                        fontSize: { md: '2.5rem', lg: '3rem' },
                        textShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      }}
                    >
                      Share Documents Securely
                    </ModernTypography>
                  </AnimatedLockContainer>

                  <Typography
                    variant="h6"
                    sx={{
                      mb: 6,
                      color: 'rgba(102, 181, 220, 0.9)',
                      fontWeight: 500,
                    }}
                  >
                    Seamless sharing with top security
                  </Typography>

                  <Box
                    component="img"
                    src={docImage}
                    alt="Secure Document Sharing"
                    sx={{
                      maxWidth: '80%',
                      height: 'auto',
                      filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.2))',
                    }}
                  />
                </Box>
              </Slide>
            </RightPanel>
          </Grid>
        </Hidden>

        <CustomModal
          type="info"
          open={showModal}
          onClose={() => setShowModal(false)}
          primaryButtonAction={() => setShowModal(false)}
          primaryButtonLabel="Ok"
          title="Help"
        >
          <Typography variant="subtitle1" color="textDisabled">
            Before logging in, download a software token authenticator on your
            mobile phone.
          </Typography>
          <ul>
            <li>
              Get the authenticator app from the{' '}
              <MUILink
                href="https://play.google.com/store/search?q=Authenticator&c=apps"
                rel="noreferrer"
                target="_blank"
                sx={{ textDecoration: 'none' }}
              >
                Play store
                <img
                  src={GooglePlayIcon}
                  alt="playstore"
                  width={20}
                  style={{ marginLeft: 5 }}
                />
              </MUILink>{' '}
              or{' '}
              <MUILink
                href="https://apps.apple.com/search?term=Authenticator"
                rel="noreferrer"
                target="_blank"
                sx={{ textDecoration: 'none' }}
              >
                App Store
                <AppleIcon />
              </MUILink>
            </li>
            <li>
              In the app select{' '}
              <span style={{ fontWeight: 600 }}>Set up account</span>
            </li>
            <li>
              Choose <span style={{ fontWeight: 600 }}>Scan barcode</span>
            </li>
          </ul>
        </CustomModal>

        <CustomModal
          type="error"
          open={showAlreadyLoggedInModal}
          onClose={() => setShowAlreadyLoggedInModal(false)}
          autoClose={false}
          primaryButtonAction={handleRedirection}
          primaryButtonLabel="Logout other session"
          secondaryButtonLabel="Continue other session"
          primaryButtonLoading={loggingOutUser}
          secondaryButtonAction={() => dispatch(logout())}
          title="Already logged in"
        >
          <Typography variant="body2">
            You are already logged in on another session or your previous
            session was not properly terminated. Would you like to log out of
            the other session and continue here?
          </Typography>
        </CustomModal>
      </Grid>
    </ModernContainer>
  );
};

export default LoginPage;
